import React, { useState, useEffect } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';

const CalendarView = ({
  selectedStartDate,
  selectedEndDate,
  onDateSelect,
  isOpen,
  onClose,
  position = 'bottom'
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [view, setView] = useState('month'); // 'month' or 'year'

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const isDateInRange = (date) => {
    if (!selectedStartDate || !selectedEndDate) return false;
    return date >= selectedStartDate && date <= selectedEndDate;
  };

  const isDateSelected = (date) => {
    if (!date) return false;
    return (selectedStartDate && date.getTime() === selectedStartDate.getTime()) ||
           (selectedEndDate && date.getTime() === selectedEndDate.getTime());
  };

  const isDateToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const handleDateClick = (date) => {
    if (!date) return;

    if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
      // Start new selection
      onDateSelect(date, 'start');
    } else if (selectedStartDate && !selectedEndDate) {
      // Complete the range
      if (date < selectedStartDate) {
        onDateSelect(date, 'end');
        onDateSelect(selectedStartDate, 'start');
      } else {
        onDateSelect(date, 'end');
      }
    }
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  const navigateYear = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setFullYear(prev.getFullYear() + direction);
      return newMonth;
    });
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
  };

  const goToMonth = (month) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(month);
      return newMonth;
    });
    setView('month');
  };

  const goToYear = (year) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setFullYear(year);
      return newMonth;
    });
    setView('month');
  };

  if (!isOpen) return null;

  const days = getDaysInMonth(currentMonth);
  const currentYear = currentMonth.getFullYear();
  const currentMonthIndex = currentMonth.getMonth();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Select Date Range</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Calendar */}
        <div className="p-4">
          {/* Month/Year Navigation */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigateMonth(-1)}
              className="p-2 hover:bg-gray-100 rounded-md"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setView('month')}
                className="text-lg font-semibold text-gray-900 hover:text-[#075e54]"
              >
                {months[currentMonthIndex]}
              </button>
              <button
                onClick={() => setView('year')}
                className="text-lg font-semibold text-gray-900 hover:text-[#075e54]"
              >
                {currentYear}
              </button>
            </div>

            <button
              onClick={() => navigateMonth(1)}
              className="p-2 hover:bg-gray-100 rounded-md"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Year View */}
          {view === 'year' && (
            <div className="grid grid-cols-4 gap-2 mb-4">
              {Array.from({ length: 12 }, (_, i) => (
                <button
                  key={i}
                  onClick={() => goToMonth(i)}
                  className={`p-2 text-sm rounded-md hover:bg-gray-100 ${
                    i === currentMonthIndex ? 'bg-[#075e54] text-white' : 'text-gray-700'
                  }`}
                >
                  {months[i].substring(0, 3)}
                </button>
              ))}
            </div>
          )}

          {/* Month View */}
          {view === 'month' && (
            <>
              {/* Week Days Header */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {weekDays.map(day => (
                  <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Days */}
              <div className="grid grid-cols-7 gap-1">
                {days.map((date, index) => {
                  if (!date) {
                    return <div key={index} className="h-10" />;
                  }

                  const isSelected = isDateSelected(date);
                  const isInRange = isDateInRange(date);
                  const isToday = isDateToday(date);

                  return (
                    <button
                      key={index}
                      onClick={() => handleDateClick(date)}
                      className={`h-10 text-sm rounded-md relative ${
                        isSelected
                          ? 'bg-[#075e54] text-white font-semibold'
                          : isInRange
                          ? 'bg-blue-100 text-blue-900'
                          : isToday
                          ? 'bg-gray-200 text-gray-900 font-semibold'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {date.getDate()}
                      {isToday && !isSelected && (
                        <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-[#075e54] rounded-full" />
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* Today Button */}
          <div className="mt-4 flex justify-center">
            <button
              onClick={goToToday}
              className="px-4 py-2 text-sm text-[#075e54] hover:bg-gray-100 rounded-md"
            >
              Go to Today
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t bg-gray-50">
          <div className="text-sm text-gray-600">
            {selectedStartDate && selectedEndDate ? (
              <span>
                {selectedStartDate.toLocaleDateString()} - {selectedEndDate.toLocaleDateString()}
              </span>
            ) : selectedStartDate ? (
              <span>Start: {selectedStartDate.toLocaleDateString()}</span>
            ) : (
              <span>Select start date</span>
            )}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm bg-[#075e54] text-white rounded-md hover:bg-[#064a42]"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarView;
